import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${({ theme }) => theme.colors.text};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.gray.light};
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin: ${({ theme }) => theme.spacing.md};
`;

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

export function ProtectedRoute({ 
  children, 
  redirectTo = '/admin/login',
  requireAuth = true 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <LoadingContainer>
        <div>
          <LoadingSpinner />
          <p style={{ marginTop: '16px' }}>Checking authentication...</p>
        </div>
      </LoadingContainer>
    );
  }

  // Show error if authentication check failed
  if (error && requireAuth) {
    return (
      <ErrorContainer>
        <h3>Authentication Error</h3>
        <p>{error}</p>
        <p>
          <a href={redirectTo}>Please try logging in again</a>
        </p>
      </ErrorContainer>
    );
  }

  // Redirect to login if not authenticated and auth is required
  if (!isAuthenticated && requireAuth) {
    // Store current location for redirect after login
    const returnTo = location.pathname + location.search;
    return (
      <Navigate 
        to={`${redirectTo}?returnTo=${encodeURIComponent(returnTo)}`} 
        replace 
      />
    );
  }

  // Redirect away from auth pages if already authenticated
  if (isAuthenticated && !requireAuth) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

// Convenience component for public routes that should redirect if authenticated
export function PublicRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireAuth={false}>
      {children}
    </ProtectedRoute>
  );
}